import { useEffect, useMemo, useRef, useState } from "react";
import { useTablesContext } from "../../context/tablesContextProvider";
import { CustomTableProps } from "../../data-model/customTableModel";
import { LoaderMessage } from "../loaderMessage/loaderMessage";
import NoData from "../noData/noData";
import { CellData } from "./table";
import TableActions from "./tableActions";
import TableWithPagination from "./tableWithPagination";
import { handleRowClickAction } from "./utils/handleRowClickAction";
import { transformRowData } from "./utils/rowDataUtils";
import { sortTableData, sortTableHeadersByOrder } from "./utils/sorting";
import updateTableDataToRender from "./utils/updateTableDataToRender";

function CustomTable(props: CustomTableProps) {
  const [computedCellData, setComputedCellData] = useState<CellData[][]>([]);
  const [startIndex, setStartIndex] = useState<number>(0);
  const [endIndex, setEndIndex] = useState<number>(
    props?.itemsPerPage ? props.itemsPerPage - 1 : 0,
  );
  const [tableDataToRender, setTableDataToRender] = useState<any[]>([]);
  const [tableWidth, setTableWidth] = useState(0);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [numberOfTimesSearchPerformed, setNumberOfTimesSearchPerformed] =
    useState<number>(0);
  const [hasInitialRenderPassed, setHasInitialRenderPassed] = useState(false);
  useEffect(() => {
    setHasInitialRenderPassed(true);
  }, []);
  const tableRef = useRef<HTMLTableElement>(null);
  const defaultValueRef = useRef<string | null>(null);
  const { getTableDataFromContext: getTableData, setTableDataForContext } =
    useTablesContext();
  const tableAttributes = getTableData(props?.tableName);

  useEffect(() => {
    const dataToRender = updateTableDataToRender(
      searchResults,
      tableAttributes,
      props?.itemsPerPage ?? 0,
      handlePageChange,
      props.shouldDisableUiSorting ?? false,
    );

    setTableDataToRender(dataToRender);
  }, [JSON.stringify(searchResults), JSON.stringify(tableAttributes)]);

  useEffect(() => {
    if (props.tableData) {
      setSearchResults(props.tableData as any[]);
    }
  }, [JSON.stringify(props.tableData)]);

  useEffect(() => {
    if (tableRef.current) {
      setTableWidth(tableRef.current.offsetWidth);
    }
  }, [tableRef.current]);

  useEffect(() => {
    const searchedTextFromContext = tableAttributes?.searchedText ?? null;
    defaultValueRef.current = searchedTextFromContext;
    props.searchingText && props.searchingText(defaultValueRef?.current ?? "");
  }, [props.tableName]);

  const onRowClick = (tableData: any, rowIndex: number, className: string) => {
    if (props.disableHoverAndClick) return;
    handleRowClickAction(tableData, rowIndex, className, startIndex, {
      onReviewButtonClick: props.onReviewButtonClick,
      onPatientDetailsClick: props.onPatientDetailsClick,
      onRowClick: props.onRowClick,
      onDeleteIconClick: props.onDeleteIconClick,
      onClickableTextWithDisabledStatusClick:
        props.onClickableTextWithDisabledStatusClick,
      onReRunOncoEmrClick: props.onReRunOncoEmrClick,
      onReRunCmmClick: props.onReRunCmmClick,
      onViewCmmClick: props.onViewCmmClick,
      onViewPharmaPaDiffCommentClick: props.onViewPharmaPaDiffCommentClick,
      onViewCommentClick: props.onViewCommentClick,
      toggleCheckboxSelection: props.toggleCheckboxSelection,
      handleDownloadButtonClick: props.handleDownloadButtonClick,
    });
  };

  const handleTableRowUpdate = (
    rowIndex: number,
    fieldKey: string,
    newValue: any,
  ) => {
    const actualGlobalRowIndex = startIndex + rowIndex;

    setSearchResults((prevData) => {
      const updatedData = [...prevData];
      if (updatedData[actualGlobalRowIndex]) {
        updatedData[actualGlobalRowIndex] = {
          ...updatedData[actualGlobalRowIndex],
          [fieldKey]: newValue,
        };
      }
      return updatedData;
    });

    setTableDataToRender((prevData) => {
      const updatedData = [...prevData];
      if (updatedData[rowIndex]) {
        updatedData[rowIndex] = {
          ...updatedData[rowIndex],
          [fieldKey]: newValue,
        };
      }
      return updatedData;
    });

    // Call the prop function if it exists
    props.onTableRowUpdate?.(rowIndex, fieldKey, newValue);
  };

  function handlePageChange(start, end, data?: any[]) {
    // Use provided data or fall back to tableDataToRender (for pagination calls without data)
    const dataToUse = data && data.length > 0 ? data : tableDataToRender;

    if (!dataToUse || dataToUse.length === 0) return;

    const validStart = Math.max(0, start);
    const validEnd = Math.min(end, dataToUse.length - 1);

    setStartIndex(validStart);
    setEndIndex(validEnd);

    if (validEnd >= validStart) {
      props.endIndexOfTable && props.endIndexOfTable(validEnd);
    }

    props.startIndexOfTable && props.startIndexOfTable(validStart);

    setComputedCellData(
      dataToUse
        .slice(validStart, validEnd + 1)
        .map((rowData) => transformRowData(rowData, sortedTableHeaders)),
    );
  }

  /**
   * Handles a sort click on a column header.
   *
   * Behavior order:
   * 1. Computes the next direction (toggle when the same column is clicked, otherwise ascending).
   * 2. Persists the sort state to context (`sortByKey`, `sortBySubKey`, `sortByField`,
   *    `sortAscending`, `sortType`) so header arrows and consumers reading context (e.g.
   *    server-side fetch hooks) stay in sync.
   * 3. If `onSortTableClicked` is provided, emits `(sortField, sortAscending)` and returns.
   *    Falls back with a dev warning if the clicked header has no `sortField`.
   * 4. Else if `shouldDisableUiSorting` is set, returns without mutating row order.
   * 5. Else runs the in-memory `sortTableData` on `searchResults`.
   *
   * @param key - The matching header's `key`
   * @param subKey - The matching header's optional `subKey`
   * @param type - Sort comparator hint ("date" | "string" | "number")
   */
  const onSortTableData = (key: string, subKey?: string, type?: string) => {
    const isSameColumn =
      tableAttributes?.sortByKey === key &&
      tableAttributes?.sortBySubKey === subKey;
    const currentSortAscending = tableAttributes?.sortAscending ?? true;
    const shouldSortAscending = isSameColumn ? !currentSortAscending : true;

    const matchingHeader = sortedTableHeaders.find(
      (header) => header.key === key && header.subKey === subKey,
    );
    const sortField = matchingHeader?.sortField;

    setTableDataForContext(props.tableName, {
      sortByKey: key,
      sortBySubKey: subKey,
      sortByField: sortField,
      sortAscending: shouldSortAscending,
      sortType: type as "date" | "string" | "number" | undefined,
    });

    if (props.onSortTableClicked) {
      if (!sortField) {
        if (process.env.NODE_ENV !== "production") {
          console.warn(
            `[CustomTable] onSortTableClicked is provided but header for key="${key}"${
              subKey ? `, subKey="${subKey}"` : ""
            } is missing a "sortField". Skipping server-side sort emit.`,
          );
        }
        return;
      }

      props.onSortTableClicked(sortField, shouldSortAscending);
      return;
    }

    if (props.shouldDisableUiSorting) {
      return;
    }

    sortTableData(
      key,
      subKey,
      searchResults,
      shouldSortAscending,
      setTableDataToRender,
      startIndex,
      endIndex,
      type,
    );
  };

  const onSearchClicked = (searchText) => {
    setNumberOfTimesSearchPerformed((prev) => prev + 1);
    setTableDataForContext(props.tableName, {
      searchedText: searchText,
    });
    props?.searchingText && props.searchingText(searchText);
  };

  const sortedTableHeaders = sortTableHeadersByOrder(props.tableHeaders);
  const countToDisplay = props.totalCount ?? props.count;

  const paginationData = useMemo(() => {
    if (countToDisplay === 0) {
      return "Gathering metadata...";
    } else {
      return props?.isFilterApplied
        ? `Filtered ${props.count ?? 0} items out of ${countToDisplay} items`
        : `Showing ${startIndex + 1} - ${Math.min(endIndex + 1, searchResults.length)} of ${countToDisplay} items`;
    }
  }, [
    countToDisplay,
    props.isFilterApplied,
    props.count,
    startIndex,
    endIndex,
    searchResults.length,
  ]);

  return (
    <div className="table-container" ref={tableRef}>
      <>
        <TableActions
          onSearchClicked={onSearchClicked}
          onRefreshButton={props.onRefreshButton}
          tableName={props.tableName}
          defaultValueRef={defaultValueRef}
          showFilterButton={props.showFilterButton}
          hideSearchBar={props.hideSearchBar}
          filterSectionsData={props.filterSectionsData}
          onFilterApplyClicked={props.onFilterApplyClicked}
          showExportButton={props.showExportButton}
          onExportButtonClick={props.onExportButtonClick}
          showRefreshButton={props.showRefreshButton}
          onBulkAssignButtonClick={props.onBulkAssignButtonClick}
          showBulkAssignButton={props.showBulkAssignButton}
          bulkAssignButtonDisabled={props.bulkAssignButtonDisabled}
          onRefetchPAOrderButtonClick={props.onRefetchPAOrderButtonClick}
          showRefetchPAOrderButton={props.showRefetchPAOrderButton}
          refetchPAOrderButtonDisabled={props.refetchPAOrderButtonDisabled}
          isRefetchingPAOrder={props.isRefetchingPAOrder}
          isDisabled={props.isDisabled}
          tableWidth={tableWidth}
          onSortTableData={onSortTableData}
          sortedTableHeaders={sortedTableHeaders}
          onSelectAll={props.onSelectAll}
          tableDataLength={
            props.tableData?.length && props.isFetching
              ? 0
              : props.tableData?.length
          }
          exportingInProgress={props.exportingInProgress}
          placeholder={props.placeholder}
          showOtherButton={props.showOtherButton}
          otherButtonMeta={props.otherButtonMeta}
          isCheckboxSelected={props.isCheckboxSelected ?? false}
          screenName={props?.screenName}
          shouldLogEvent={props?.shouldLogEvent ?? false}
        />
      </>

      {props.isFetching || !hasInitialRenderPassed ? (
        <LoaderMessage message="Fetching Data ..." />
      ) : (
        <>
          {searchResults.length > 0 ? (
            <>
              {
                <TableWithPagination
                  tableName={props.tableName}
                  sortedTableHeaders={sortedTableHeaders}
                  computedCellData={computedCellData}
                  tableDataToRender={tableDataToRender}
                  tableWidth={tableWidth}
                  onSortTableData={onSortTableData}
                  onRowClick={onRowClick}
                  onAssignToClick={props.onAssignToClick}
                  onStatusClick={props.onStatusClick}
                  hidePagination={props.hidePagination}
                  count={props.count ?? undefined}
                  itemsPerPage={props.itemsPerPage}
                  pagesPerView={props.pagesPerView}
                  handlePageChange={handlePageChange}
                  triggerHandlePageChange={
                    numberOfTimesSearchPerformed +
                    (props.requestForPaginationReset ?? 0)
                  }
                  paginationData={paginationData}
                  onDeleteIconClick={props.onDeleteIconClick}
                  onEditIconClick={props.onEditIconClick}
                  onIconCellClick={props.onIconCellClick}
                  onClickableTextWithDisabledStatusClick={
                    props.onClickableTextWithDisabledStatusClick
                  }
                  showInLineLoader={props.showInLineLoader}
                  onComplete={props.onComplete}
                  onClickableBadgeClick={props.onClickableBadgeClick}
                  onSelectAll={props.onSelectAll}
                  toggleCheckboxSelection={props.toggleCheckboxSelection}
                  handleDownloadButtonClick={props.handleDownloadButtonClick}
                  onTableRowUpdate={handleTableRowUpdate}
                  startIndex={startIndex}
                  isPreviousButtonDisabled={props.isPreviousButtonDisabled}
                  onSideModalClose={props.onSideModalClose}
                  onChangeEmit={props.onChangeEmit}
                  onPropagateEventOnClickInRpaStatusCell={
                    props.onPropagateEventOnClickInRpaStatusCell
                  }
                  onPropagateEventOnClickInRpaStatusTwoCell={
                    props.onPropagateEventOnClickInRpaStatusTwoCell
                  }
                  onRpaStatusForExternalClick={
                    props.onRpaStatusForExternalClick
                  }
                  onValueChangeOfTextField={props.onValueChangeOfTextField}
                  onOverlappingIconClick={props.onOverlappingIconClick}
                  rowClassName={props.rowClassName}
                  onUploadClick={props.onUploadClick}
                  onSuccessClick={props.onSuccessClick}
                  handleIconClickOnDocumentScreenshot={
                    props.handleIconClickOnDocumentScreenshot
                  }
                  onSearchChange={props.onSearchChange}
                  onCreateOption={props.onCreateOption}
                  onOptionClick={props.onOptionClick}
                />
              }
            </>
          ) : (
            <NoData />
          )}
        </>
      )}
    </div>
  );
}

export default CustomTable;
